import React from 'react';
import { ProjectedTaskDetail } from '../../../../interfaces/adminUser/adminRecurringTaskStats/AdminRecurringTaskJobStats';

interface DetailListProps {
    tasks: ProjectedTaskDetail[];
    isMissedView?: boolean;
}

const DetailList: React.FC<DetailListProps> = ({ tasks, isMissedView = false }) => {
    if (!tasks || tasks.length === 0) return <div className="text-gray-500 italic">No tasks found for this selection.</div>;

    return (
        <div className="space-y-4">
            {tasks.map((task, idx) => (
                <div key={idx} className={`p-4 rounded-lg border border-l-4 ${isMissedView ? 'border-l-red-500 bg-red-50' : 'border-l-blue-500 bg-gray-50'}`}>
                    
                    {/* Header: Title and Name */}
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <h3 className="font-bold text-gray-800 text-lg">{task.templateTitle || "Untitled Task"}</h3>
                            <div className="text-sm text-gray-600">
                                <span className="font-semibold text-gray-500">Assignee: </span> 
                                {task.employeeName || "Unknown Employee"}
                            </div>
                            {task.participantName && (
                                <div className="text-sm text-gray-600">
                                    <span className="font-semibold text-gray-500">Participant: </span> 
                                    {task.participantName}
                                </div>
                            )}
                        </div>
                        <div className="text-right">
                             <div className="text-xs font-mono bg-white border px-2 py-1 rounded text-gray-500">
                                Template ID: {task.templateId}
                            </div>
                        </div>
                    </div>
                    
                    {/* Grid for Details */}
                    <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm bg-white p-3 rounded border">
                        
                        {/* 1. Trigger Pattern */}
                        <div>
                            <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Trigger Pattern</span>
                            <span className="text-gray-800 font-medium">
                                {task.triggerPattern ? task.triggerPattern : <span className="text-gray-400 italic">N/A</span>}
                            </span>
                        </div>

                        {/* 2. Trigger Reason */}
                        <div>
                            <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Logic</span>
                            <span className="text-gray-800 truncate" title={task.reason}>
                                {task.reason}
                            </span>
                        </div>

                        {/* 3. Projected Creation Date */}
                        <div>
                            <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Creation Date</span>
                            <span className="text-blue-700 font-bold">
                                {new Date(task.projectedCreationDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                        </div>

                        {/* 4. Projected Due Date */}
                        <div>
                            <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Due Date</span>
                            <span className={`${task.projectedDueDate ? 'text-gray-800' : 'text-gray-400 italic'}`}>
                                {task.projectedDueDate 
                                    ? new Date(task.projectedDueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) 
                                    : 'None Calculated'}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default DetailList;

// Wrapper for the Retroactive Tab
export const MissedTasksView: React.FC<{ tasks: ProjectedTaskDetail[] }> = ({ tasks }) => {
    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4 text-red-600">Retroactive Missed Tasks</h2>
            <p className="text-sm text-gray-600 mb-6">
                These tasks met their criteria (e.g. "7 Days After Join") in the past 90 days, 
                but were never created in the database.
            </p>
            <DetailList tasks={tasks} isMissedView={true} />
        </div>
    );
}